import { useState } from 'react'
import { Download, Check, AlertCircle } from 'lucide-react'
import { useDownloadBugReports } from '../../hooks/bug-management/useDownloadBugReports'
import './DownloadBugReportsButton.css'

interface DownloadBugReportsButtonProps {
  projectId: string
  status?: string
  severitySort?: 'asc' | 'desc' | null
  severity?: string
  kind?: string
  environment?: string
  disabled?: boolean
  className?: string
}

// "Download Bug Reports" companion to CopyBugReportsButton. Gathers the same set of open bugs and
// saves them as a single markdown file named "<project name>_all-bugs.md". Shown wherever the Copy
// button appears so users can either paste into a coding agent or keep a file.
export default function DownloadBugReportsButton({ projectId, status, severitySort, severity, kind, environment, disabled, className }: DownloadBugReportsButtonProps) {
  // Mirror the Copy button's states: the fetch or the file write can throw, and a distinct 'failed'
  // state lets the user tell a failed download from a successful one.
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'failed'>('idle')
  const downloadBugReports = useDownloadBugReports(projectId)

  async function handleDownload() {
    setState('loading')
    const ok = await downloadBugReports({ status, severitySort, severity, kind, environment })
    if (ok) {
      setState('done')
      setTimeout(() => setState('idle'), 2000)
    } else {
      setState('failed')
      setTimeout(() => setState('idle'), 4000)
    }
  }

  const label = state === 'loading'
    ? 'Downloading...'
    : state === 'done'
      ? 'Downloaded'
      : state === 'failed'
        ? "Couldn't download — try again"
        : 'Download Bug Reports'

  return (
    <button
      type="button"
      className={`download-bug-reports-btn${state === 'failed' ? ' download-bug-reports-btn--failed' : ''}${className ? ` ${className}` : ''}`}
      onClick={handleDownload}
      disabled={disabled || state === 'loading'}
      data-testid="download-all-markdown-btn"
      data-analytics-event="bug-download-reports"
      aria-label={label}
    >
      {state === 'done'
        ? <Check size={14} aria-hidden="true" />
        : state === 'failed'
          ? <AlertCircle size={14} aria-hidden="true" />
          : <Download size={14} aria-hidden="true" />}
      {label}
      {/* Announce a failure to assistive tech — the label change alone isn't in a live region. */}
      <span role="status" aria-live="polite" className="sr-only">
        {state === 'failed' ? 'Downloading bug reports failed' : ''}
      </span>
    </button>
  )
}
